/**
 * @file ContentContext.jsx
 * @description Context global que gerencia o estado do workflow.
 */

import { createContext, useContext, useState, useCallback, useRef } from 'react';
import WorkflowRunAPI from '../services/WorkflowRunAPI';
import { getAuthToken } from '../services/TokenService';

const ContentContext = createContext(null);

export const RUN_STATUS = {
  WAITING: 'waiting',
  RUNNING: 'running',
  STOPPED: 'stopped',
  SUCCEEDED: 'succeeded',
  ERROR: 'error',
};

export const RUN_STATUS_LABELS = {
  waiting: 'Waiting',
  running: 'Running',
  stopped: 'Stopped',
  succeeded: 'Succeeded',
  error: 'Error',
};

export const RUN_STATUS_COLORS = {
  waiting: '#497e9a',
  running: '#df913c',
  stopped: '#6648c2',
  succeeded: '#599764',
  error: '#c23c3c',
};

export const RUN_STATUS_LABELS_COLORS = {
  waiting: '#83D7E6',
  running: '#FBE079',
  stopped: '#AC9BF6',
  succeeded: '#A2E6A2',
  error: '#d76c6cff',
};

const normalizeNodeName = (name) => name
  ? name
      .toLowerCase()
      .replace(/_/g, ' ')
      .replace(/#/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
  : '';

const FIXED_STEP_IDS = new Set(['start', 'feedback', 'download']);

const isProcessStepId = (stepId) => !!stepId && !FIXED_STEP_IDS.has(stepId);

const isSpecialNodeTitle = (title) => {
  const normalizedTitle = normalizeNodeName(title);
  return ['start', 'end', 'if else', 'human feedback'].includes(normalizedTitle);
};

export const getProcessStepIds = (steps = []) =>
  steps.filter((step) => isProcessStepId(step.id)).map((step) => step.id);

export const getLastProcessStepId = (steps = []) => {
  const ids = getProcessStepIds(steps);
  return ids.length ? ids[ids.length - 1] : null;
};

const ensureDynamicProcessStep = (steps, stepId) => {
  if (!isProcessStepId(stepId) || steps.some(step => step.id === stepId)) {
    return steps;
  }

  const dynamicStep = { id: stepId, label: stepId, status: 'pending' };
  const feedbackIndex = steps.findIndex(step => step.id === 'feedback');

  if (feedbackIndex === -1) {
    return [...steps, dynamicStep];
  }

  return [...steps.slice(0, feedbackIndex), dynamicStep, ...steps.slice(feedbackIndex)];
};

const TEMPLATE_MAP = {
  'Template Standard MIA': 'STANDARD',
  'Template 1 Onco BR': 'ONCO',
  'Template 1 Onco MX': 'ONCO',
};

export const WORKFLOW_MATRIX = {
  'BR-email-STANDARD': import.meta.env.VITE_WORKFLOW_EMAIL_STANDARD_BR,
  'BR-email-ONCO': import.meta.env.VITE_WORKFLOW_EMAIL_ONCO_BR,
  'BR-separata': import.meta.env.VITE_WORKFLOW_SEPARATA_BR,
  'BR-news': import.meta.env.VITE_WORKFLOW_NEWS_BR,
  'BR-whatsapp': import.meta.env.VITE_WORKFLOW_WHATSAPP_BR,

  'MX-email-STANDARD': import.meta.env.VITE_WORKFLOW_EMAIL_STANDARD_MX,
  'MX-email-ONCO': import.meta.env.VITE_WORKFLOW_EMAIL_ONCO_MX,
  'MX-separata': import.meta.env.VITE_WORKFLOW_SEPARATA_MX,
  'MX-news': import.meta.env.VITE_WORKFLOW_NEWS_MX,
  'MX-whatsapp': import.meta.env.VITE_WORKFLOW_WHATSAPP_MX
};

export const getWorkflowId = (compliance, format, template) => {
  const normalizedCompliance = compliance === 'Mexico' ? 'MX' : 'BR';

  let key = `${normalizedCompliance}-${format}`;

  if (format === 'email') {
    key += `-${TEMPLATE_MAP[template]}`;
  }

  return WORKFLOW_MATRIX[key] || '';
};

/**
 * Decide quais parcelas file/text vão para a API conforme escolha do modal (`contextMode`).
 * Sem `contextMode` explícito, infere só com base no que existe (compatível com configs antigas).
 */
const resolveContextPayloadMode = (data) => {
  const explicit = data.contextMode ?? data.data?.contextMode;
  if (explicit && ['file', 'text', 'both'].includes(explicit)) {
    return explicit;
  }
  const rawContext = String(data.context ?? data.data?.context ?? '').trim();
  const fileId = data.fileId ?? data.data?.fileId;
  const fileName = data.fileName ?? data.data?.fileName;
  const hasFile = fileId != null && fileName != null && String(fileName).length > 0;
  if (hasFile && rawContext) return 'both';
  if (hasFile) return 'file';
  if (rawContext) return 'text';
  return 'file';
};

const createStandardInputBuilder = () => (data) => {
  const complianceValue = data.compliance || data.data?.compliance;
  const outputLang = data.outputLanguage || data.data?.outputLanguage;
  const mode = resolveContextPayloadMode(data);
  const fileId = data.fileId ?? data.data?.fileId;
  const fileName = data.fileName ?? data.data?.fileName;
  const wantsFile = mode === 'file' || mode === 'both';
  const wantsContextText = mode === 'text' || mode === 'both';
  const hasFile =
    wantsFile &&
    fileId != null &&
    fileName != null &&
    String(fileName).length > 0;
  const rawContext = String(data.context ?? data.data?.context ?? '').trim();
  const contextStr = wantsContextText ? rawContext : '';
  const isPtBr = outputLang === 'Portuguese' && complianceValue === 'Brazil';

  const input = {
    Output_Language: outputLang,
  };

  // Start node expects Input_Content even when optional; match MGA payload (empty strings when no file).
  input.Input_Content = hasFile
    ? { file_id: fileId, file_name: fileName }
    : { file_id: '', file_name: '' };

  if (complianceValue === 'Mexico') {
    input.ComplianceCheck = complianceValue;
  } else if (isPtBr) {
    input.ComplianceCheck = 'Brazil';
    // Pt-BR: Context só quando o modo inclui texto; vazio para só arquivo.
    input.Context = contextStr;
    // UI label no modal é "Product"; a API MyGen Assist espera sempre a chave `Brand`.
    input.Brand = String(data.brand ?? data.data?.brand ?? '').trim();
  }

  return input;
};

const createEmailInputBuilder = () => (data) => {
  const templateId = data.emailSettings?.template || data.data?.emailSettings?.template;
  const complianceRaw = data.compliance || data.data?.compliance;
  let complianceValue = complianceRaw === 'No compliance' ? '' : complianceRaw;
  let content = templateId == 'Template Standard MIA' && complianceValue == 'Mexico' ? 'Content' : 'Input_Content';
  const mode = resolveContextPayloadMode(data);
  const fileId = data.fileId ?? data.data?.fileId;
  const fileName = data.fileName ?? data.data?.fileName;
  const wantsFile = mode === 'file' || mode === 'both';
  const wantsContextText = mode === 'text' || mode === 'both';
  const hasFile =
    wantsFile &&
    fileId != null &&
    fileName != null &&
    String(fileName).length > 0;
  const outputLang = data.outputLanguage || data.data?.outputLanguage;
  const isPtBr = outputLang === 'Portuguese' && complianceRaw === 'Brazil';
  const rawContext = String(data.context ?? data.data?.context ?? '').trim();
  const contextStr = wantsContextText ? rawContext : '';

  const input = {
    Email_Template: templateId,
    Output_Language: outputLang,
    ComplianceCheck: complianceValue
  };

  // Start node expects file field even when optional (same as MGA): empty strings when no file.
  input[content] = hasFile
    ? { file_id: fileId, file_name: fileName }
    : { file_id: '', file_name: '' };

  if (isPtBr) {
    input.Context = contextStr;
    // UI label no modal é "Product"; a API MyGen Assist espera sempre a chave `Brand`.
    input.Brand = String(data.brand ?? data.data?.brand ?? '').trim();
  }

  return input;
};

const createSeparataInputBuilder = () => createStandardInputBuilder();

export const getWorkflowConfig = (format, workflowId, apiNodes = []) => {
  const nodeStrings = apiNodes.map(node => 
    typeof node === 'string' ? node : (node?.id || node?.title || node?.label || String(node))
  );
  const dynamicSteps = nodeStrings.map(title => ({ id: title, label: title }));

  const configs = {
    email: {
      id: workflowId,
      steps: [{ id: 'start', label: 'Start' }, ...dynamicSteps, { id: 'feedback', label: 'Feedback' }, { id: 'download', label: 'Download' }],
      inputBuilder: createEmailInputBuilder()
    },
    separata: {
      id: workflowId,
      steps: [{ id: 'start', label: 'Start' }, ...dynamicSteps, { id: 'feedback', label: 'Feedback' }, { id: 'download', label: 'Download' }],
      inputBuilder: createSeparataInputBuilder()
    },
    news: {
      id: workflowId,
      steps: [{ id: 'start', label: 'Start' }, ...dynamicSteps, { id: 'feedback', label: 'Feedback' }, { id: 'download', label: 'Download' }],
      inputBuilder: createStandardInputBuilder()
    },
    whatsapp: {
      id: workflowId,
      steps: [{ id: 'start', label: 'Start' }, ...dynamicSteps, { id: 'feedback', label: 'Feedback' }, { id: 'download', label: 'Download' }],
      inputBuilder: createStandardInputBuilder()
    }
  };

  return configs[format];
};
export const PROCESS_STEPS = ['start', 'content-creation', 'compliance-check', 'image-creation', 'html-creation'];

const createInitialRunState = () => ({
  runId: null,
  isRunning: false,
  status: RUN_STATUS.WAITING,
  currentStep: null,
  currentMessage: '',
  config: null,
  steps: [],
  results: null,
  error: null,
});

export const ContentProvider = ({ children }) => {
  const [contents, setContents] = useState([]);
  const [runProgress, setRunProgress] = useState(createInitialRunState);
  const [activeConfig, setActiveConfig] = useState(null);
  const runController = useRef(null);

const mapHistoryMessages = (config, messages) => {
  const contentMap = {};
  
  (Array.isArray(messages) ? messages : []).forEach(msg => {
    const normalizedName = normalizeNodeName(msg.name);
    if (['if else', 'human feedback'].includes(normalizedName)) {
      return
    }
    const stepId = msg.name.replace(/_/g, ' ');
    if (stepId) {
      contentMap[stepId] = msg.content;
    }
  });
  
  return contentMap;
};

  const setRunStateFromExistingRun = useCallback(async (existingRunId, config, existingOutput = null) => {
    const formatKey = config?.selectedFormat?.toLowerCase();
    
    setActiveConfig(config);
    const lastPid = getLastProcessStepId(config.steps || []);
    let existingResults = null;
    
    if (existingOutput) {
      existingResults = { success: true, message: existingOutput, timestamp: new Date().toISOString() };
    } else {
      try {
        
        const token = await getAuthToken();
        const response = await fetch(`https://chat.int.bayer.com/api/v3/workflows/runs/${existingRunId}`, {
          headers: { accept: 'application/json', Authorization: `Bearer ${token}` }
        });
        
        if (response.ok) {
          const runData = await response.json();
          const outputContent = runData.outputs?.messages?.[0]?.content;
          if (outputContent) {
            existingResults = { success: true, message: outputContent, timestamp: new Date().toISOString(), rawData: runData };
          }
        }
      } catch (error) {
        console.error('Error fetching run data:', error);
      }
    }
    
    setRunProgress({
      runId: existingRunId,
      isRunning: false,
      status: RUN_STATUS.WAITING,
      currentStep: lastPid || 'download',
      currentMessage: '',
      config,
      steps: (config.steps || []).map(step => {
        if (step.id === 'feedback') return { ...step, status: 'completed', message: undefined };
        if (step.id === 'start')
          return { ...step, status: 'completed' };
        if (lastPid && isProcessStepId(step.id)) {
          const outMsg = existingResults?.message || undefined;
          return {
            ...step,
            status: step.id === lastPid ? 'waiting' : 'completed',
            message: step.id === lastPid ? outMsg : undefined,
          };
        }
        return { ...step, status: 'pending' };
      }),
      results: existingResults,
      error: null,
    });
  }, []);

  const setRunStateForDownload = useCallback((runId, config, messages) => {
    setActiveConfig(config);
    const contentMap = mapHistoryMessages(config, messages);
    const lastMsg = Array.isArray(messages) ? messages[messages.length - 1]?.content : "";

    setRunProgress({
      runId, config, isRunning: false, status: RUN_STATUS.SUCCEEDED, currentStep: 'download',
      results: { message: lastMsg },
      steps: config.steps.map(s => ({ ...s, status: 'completed', message: contentMap[s.id] || (s.id === 'download' ? lastMsg : null) }))
    });
  }, []);

  const setRunStateForFeedback = useCallback((runId, config, messages) => {
    setActiveConfig(config);
    const contentMap = mapHistoryMessages(config, messages);
    const lastMsg = Array.isArray(messages) ? messages[messages.length - 1]?.content : '';
    const lastPid = getLastProcessStepId(config.steps || []);

    setRunProgress({
      runId,
      config,
      isRunning: false,
      status: RUN_STATUS.WAITING,
      currentStep: lastPid || config.steps?.[1]?.id || 'download',
      results: { message: lastMsg, allStepContents: { ...contentMap } },
      steps: config.steps.map(s => {
        if (s.id === 'feedback') return { ...s, status: 'completed', message: undefined };
        if (s.id === 'start') return { ...s, status: 'completed', message: contentMap[s.id] };
        if (lastPid && isProcessStepId(s.id)) {
          return {
            ...s,
            status: s.id === lastPid ? 'waiting' : 'completed',
            message: contentMap[s.id] || (s.id === lastPid ? lastMsg : undefined),
          };
        }
        if (s.id === 'download') return { ...s, status: 'pending', message: undefined };
        return { ...s, status: 'pending' };
      }),
    });
  }, []);

  const addContent = useCallback((content) => {
    setContents(prev => [{ ...content, id: Date.now().toString(), lastModified: new Date().toLocaleDateString('pt-BR') }, ...prev]);
  }, []);

  const deleteContent = useCallback((id) => setContents(prev => prev.filter(c => c.id !== id)), []);

  const startRun = useCallback(async (config, extra = {}) => {
    const formatKey = (config?.selectedFormat || '').toLowerCase();
    const workflowId = getWorkflowId(
      config.compliance,
      formatKey,
      config.emailSettings?.template
    );

    if (!workflowId) return;

    const currentConfig = getWorkflowConfig(formatKey, workflowId, []);

    if (!currentConfig) return;

    setActiveConfig(currentConfig);

    setRunProgress({
      ...createInitialRunState(),
      isRunning: true,
      status: RUN_STATUS.RUNNING,
      currentStep: 'start',
      config: config,
      steps: currentConfig.steps.map((s, i) => ({ ...s, status: i === 0 ? 'in_progress' : 'pending' }))
    });
    
    const controller = new AbortController();
    runController.current = controller;

    const stepContents = {};
    let humanFeedbackEncountered = false;

    const onProgress = (data) => {
      if (!runController.current) return;

      const incomingId = data.run_id || data.runId || data.node?.run_id || data.node?.data?.run_id;
      const outputs = data.outputs || data.node?.data?.outputs || data.node?.outputs;

      const rawTitle = data.node?.data?.title || data.node?.title || '';
      const nodeStatus = data.node?.data?.status || data.node?.status;
      const normalizedTitle = normalizeNodeName(rawTitle);

      if (normalizedTitle === 'human feedback') {
        humanFeedbackEncountered = true;

        const finalContent = Object.values(stepContents).pop() || '';

        setRunProgress(prev => {
          const lastPid = getLastProcessStepId(prev.steps);
          return {
            ...prev,
            runId: incomingId || prev.runId,
            currentStep: lastPid || prev.currentStep,
            status: RUN_STATUS.WAITING,
            isRunning: false,
            steps: prev.steps.map(s => {
              if (s.id === 'start') {
                return { ...s, status: 'completed' };
              }
              if (isProcessStepId(s.id)) {
                if (lastPid && s.id === lastPid) {
                  return { ...s, status: 'waiting', message: finalContent };
                }
                return {
                  ...s,
                  status: 'completed',
                  message: stepContents[s.id] || s.message,
                };
              }
              if (s.id === 'feedback') {
                return { ...s, status: 'completed', message: undefined };
              }
              return s;
            }),
            results: {
              success: true,
              message: finalContent,
              timestamp: new Date().toISOString(),
              allStepContents: { ...stepContents },
            },
          };
        });
        return;
      }

      if (isSpecialNodeTitle(rawTitle)) {
        return;
      }

      const stepId = rawTitle;

      if (outputs?.messages?.length > 0 && stepId) {
        stepContents[stepId] = outputs.messages[outputs.messages.length - 1]?.content || '';
      }

      if (stepId || incomingId) {
        setRunProgress(prev => {
          const stepsWithDynamic = stepId
            ? ensureDynamicProcessStep(prev.steps, stepId)
            : prev.steps;
          const allIds = stepsWithDynamic.map(s => s.id);
          const currentIdx = allIds.indexOf(stepId);

          return {
            ...prev,
            runId: incomingId || prev.runId,
            currentStep: stepId || prev.currentStep,
            steps: stepsWithDynamic.map((step, idx) => {
              if (step.id === 'start' && stepId && stepId !== 'start') {
                return { ...step, status: 'completed' };
              }

              if (currentIdx !== -1 && idx < currentIdx && isProcessStepId(step.id)) {
                if (stepContents[step.id] || step.status === 'completed') {
                  return { ...step, status: 'completed' };
                }
                return step;
              }

              if (step.id === stepId) {
                return {
                  ...step,
                  status: nodeStatus === 'succeeded' ? 'completed' : 'in_progress',
                  message: stepContents[stepId] || step.message
                };
              }
              return step;
            }),
            results: stepContents[stepId] ? {
              success: true,
              message: stepContents[stepId],
              allStepContents: { ...stepContents }
            } : prev.results
          };
        });
      }
    };

    setRunProgress(prev => ({
      ...createInitialRunState(),
      isRunning: true,
      status: RUN_STATUS.RUNNING,
      currentStep: 'start',
      config: config || prev.config,
      steps: currentConfig.steps.map((s, i) => ({ ...s, status: i === 0 ? 'in_progress' : 'pending' }))
    }));

    try {
      const result = await WorkflowRunAPI.runWorkflow(
        currentConfig.id,
        currentConfig.inputBuilder(config),
        onProgress,
        { signal: controller.signal }
      );

      if (controller.signal.aborted) return;

      if (!humanFeedbackEncountered) {
        const finalContent = Object.values(stepContents).pop() || '';

        setRunProgress(prev => {
          const processStepIds = getProcessStepIds(prev.steps);
          const lastProcessStep = processStepIds[processStepIds.length - 1] || prev.currentStep || 'start';

          return {
            ...prev,
            status: RUN_STATUS.SUCCEEDED,
            currentStep: lastProcessStep,
            isRunning: false,
            steps: prev.steps.map(s => {
              if (isProcessStepId(s.id) || s.id === 'start') {
                return {
                  ...s,
                  status: 'completed',
                  message: stepContents[s.id] || s.message
                };
              }
              return s;
            }),
            results: {
              success: true,
              message: finalContent,
              timestamp: new Date().toISOString(),
              rawData: result,
              allStepContents: { ...stepContents }
            }
          };
        });
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        setRunProgress(prev => ({ ...prev, status: RUN_STATUS.ERROR, error: error.message, isRunning: false }));
      }
    } finally {
      setRunProgress(prev => ({ ...prev, isRunning: false }));
      runController.current = null;
    }
  }, []);

  const generateMaterials = useCallback(async () => {  
    const { runId, isRunning, currentStep } = runProgress;

    if (!runId || isRunning) return;
    const allConfigSteps = runProgress.steps.map(s => s.id);

    const controller = new AbortController();
    runController.current = controller;

    const processIds = getProcessStepIds(runProgress.steps);
    const lastProcessStepId = processIds[processIds.length - 1];

    let nextStepId;

    if (currentStep === 'feedback' || currentStep === lastProcessStepId) {
      nextStepId = 'download';
    } else {
      const currentIndex = allConfigSteps.indexOf(currentStep);
      nextStepId = currentIndex >= 0 && currentIndex < allConfigSteps.length - 1
        ? allConfigSteps[currentIndex + 1]
        : currentStep;
    }
    
    const nextStepIndex = allConfigSteps.indexOf(nextStepId);

    setRunProgress(prev => {
      const newSteps = prev.steps.map(step => {
        const stepIndex = allConfigSteps.indexOf(step.id);

        if (stepIndex < nextStepIndex) {
          return { ...step, status: 'completed' };
        }

        if (step.id === nextStepId) {
          const status = step.id === 'download' ? 'waiting' : 'in_progress';
          return { ...step, status: status, message: undefined };
        }

        if (stepIndex > nextStepIndex) {
          return { ...step, status: 'pending', message: undefined };
        }

        return step;
      });

      return {
        ...prev,
        isRunning: true,
        status: RUN_STATUS.RUNNING,
        currentStep: nextStepId,
        error: null,
        steps: newSteps
      };
    });

    try {
      const stepContents = {};

      const onProgress = (data) => {
      if (!runController.current) return;

      const nodeTitle = data.node?.data?.title || '';
      const nodeStatus = data.node?.data?.status;
      const outputs = data.outputs || data.node?.data?.outputs || data.node?.outputs;

      const normalizedTitle = normalizeNodeName(nodeTitle);
      if (['if else', 'human feedback', 'start', 'end'].includes(normalizedTitle)) {
        return;
      }
      const stepId = nodeTitle;

      if (outputs?.messages?.length > 0 && stepId) {
        const lastMessage = outputs.messages[outputs.messages.length - 1];
        if (lastMessage?.content) {
          let rawContent = lastMessage.content;
          const firstTagIndex = rawContent.indexOf('<');
          stepContents[stepId] = (firstTagIndex > 0) ? rawContent.substring(firstTagIndex) : rawContent;
        }
      }

      if (stepId) {
        setRunProgress(prev => {
          if (prev.status === RUN_STATUS.STOPPED) return prev;

          const stepsWithDynamic = ensureDynamicProcessStep(prev.steps, stepId);
          const dynamicStepIds = stepsWithDynamic.map(step => step.id);
          const apiStepIndex = dynamicStepIds.indexOf(stepId);

          return {
            ...prev,
            currentStep: stepId,
            currentMessage: nodeTitle,
            steps: stepsWithDynamic.map(step => {
              const localIndex = dynamicStepIds.indexOf(step.id);

              // Only mark previous steps as completed if they were actually processed
              if (localIndex < apiStepIndex) {
                // Only mark as completed if this step was actually processed or already completed
                if (stepContents[step.id] || step.status === 'completed') {
                  return { ...step, status: 'completed' };
                }
                return step;
              }

              if (step.id === stepId) {
                return {
                  ...step,
                  status: nodeStatus === 'succeeded' ? 'completed' : 'in_progress',
                  message: stepContents[stepId] || step.message
                };
              }
              return step;
            }),
            results: stepContents[stepId] ? {
              success: true,
              message: stepContents[stepId],
              timestamp: new Date().toISOString(),
              rawData: null,
              allStepContents: { ...stepContents }
            } : prev.results,
          };
        });
      }
    };

      let result;
      try {
        result = await WorkflowRunAPI.generateMaterial(
          runId,
          onProgress,
          { signal: controller.signal }
        );
      } catch (error) {
        const isAlreadySucceeded = 
          error.response?.status === 400 && 
          (error.response?.data?.detail === "Workflow run has already succeeded. Please create a new run."
            || error.response?.data?.detail === "Workflow run is not in a waiting state.");

        if (isAlreadySucceeded) {
          result = { status: 'already_succeeded' };
        } else {
          console.log('Error caught:', error);
          if (error.name !== 'AbortError') {
            setRunProgress(prev => ({ ...prev, status: RUN_STATUS.ERROR, error: error.message, isRunning: false }));
          }
          return;
        }
        return;
      }

      if (result === null) return;
      if (controller.signal.aborted) return;

      setRunProgress(prev => {
        if (prev.status === RUN_STATUS.STOPPED) return prev;

        let finalContent = Object.values(stepContents).pop();

        if (!finalContent && result) {
            const possibleMessageArrays = [
                result.node?.outputs?.messages,
                result.outputs?.messages,
                result.node?.data?.outputs?.messages
            ];
            const foundMessages = possibleMessageArrays.find(arr => Array.isArray(arr) && arr.length > 0);
            
            if (foundMessages) {
                finalContent = foundMessages[foundMessages.length - 1]?.content;
            }
        }

        if (!finalContent) {
             finalContent = prev.results?.message;
        }

        finalContent = finalContent || '';

        let finalResults;
        if (finalContent) {
          finalResults = {
            success: true,
            message: finalContent,
            timestamp: new Date().toISOString(),
            rawData: result,
            allStepContents: { ...stepContents },
          };
        } else {
          finalResults = { success: false, message: 'No content generated' };
        }

        return {
          ...prev,
          status: RUN_STATUS.SUCCEEDED,
          currentStep: 'download',
          isRunning: false,
          currentMessage: '',
          steps: prev.steps.map(step => {
            let messageToSave = stepContents[step.id] || step.message;

            if (!messageToSave && (step.id === 'download' || step.id === prev.currentStep || step.id === 'html-creation')) {
                messageToSave = finalContent;
            }

            return { 
              ...step, 
              status: 'completed',
              message: messageToSave
            };
          }),
          results: finalResults,
        };
      });

    } catch (error) {
      console.error('Generate materials error:', error);
    } finally {
      setRunProgress(prev => ({ ...prev, isRunning: false }));
      runController.current = null;
    }
  }, [runProgress.runId, runProgress.isRunning, runProgress.currentStep, runProgress.steps]);
  

    /**
   * Melhora o conteúdo gerado com feedback do usuário usando o endpoint /regenerate
   * @param {string} userMessage - Mensagem de feedback do usuário
   */
  const improveContent = useCallback(async (userMessage) => {
    const { runId, isRunning } = runProgress;
    
    if (!runId || isRunning || !userMessage?.trim()) {
      console.warn('Cannot improve: missing runId, already running, or empty message');
      return;
    }
    
    const PROCESS_STEPS_LIST = getProcessStepIds(runProgress.steps);
    const controller = new AbortController();
    runController.current = controller;
    
    const stepContents = {};
    let humanFeedbackEncountered = false;
    
    const onProgress = (data) => {
      if (!runController.current) return;
      
      const outputs = data.outputs || data.node?.data?.outputs || data.node?.outputs;
      
      const rawTitle = data.node?.data?.title || data.node?.title || '';
      const nodeStatus = data.node?.data?.status || data.node?.status;
      
      const normalizedTitle = normalizeNodeName(rawTitle);
      if (normalizedTitle === 'human feedback') {
        humanFeedbackEncountered = true;

        const finalContent = Object.values(stepContents).pop() || '';

        setRunProgress(prev => {
          const lastPid = getLastProcessStepId(prev.steps);
          return {
            ...prev,
            currentStep: lastPid || prev.currentStep,
            status: RUN_STATUS.WAITING,
            isRunning: false,
            steps: prev.steps.map(s => {
              if (s.id === 'start') {
                return { ...s, status: 'completed' };
              }
              if (isProcessStepId(s.id)) {
                if (lastPid && s.id === lastPid) {
                  return { ...s, status: 'waiting', message: finalContent };
                }
                return {
                  ...s,
                  status: 'completed',
                  message: stepContents[s.id] || s.message,
                };
              }
              if (s.id === 'feedback') {
                return { ...s, status: 'completed', message: undefined };
              }
              return s;
            }),
            results: {
              success: true,
              message: finalContent,
              timestamp: new Date().toISOString(),
              allStepContents: { ...stepContents },
            },
          };
        });
        return;
      }
      
      if (isSpecialNodeTitle(rawTitle)) {
        return;
      }
      
      const stepId = rawTitle;
      
      if (outputs?.messages?.length > 0 && stepId) {
        stepContents[stepId] = outputs.messages[outputs.messages.length - 1]?.content || '';
      }
      
      if (stepId) {
        setRunProgress(prev => {
          const stepsWithDynamic = ensureDynamicProcessStep(prev.steps, stepId);
          const allIds = stepsWithDynamic.map(s => s.id);
          const currentIdx = allIds.indexOf(stepId);

          return {
            ...prev,
            currentStep: stepId,
            steps: stepsWithDynamic.map((step, idx) => {
              if (step.id === 'start' && stepId && stepId !== 'start') {
                return { ...step, status: 'completed' };
              }

              if (currentIdx !== -1 && idx < currentIdx && isProcessStepId(step.id)) {
                if (stepContents[step.id] || step.status === 'completed') {
                  return { ...step, status: 'completed' };
                }
                return step;
              }

              if (step.id === stepId) {
                return {
                  ...step,
                  status: nodeStatus === 'succeeded' ? 'completed' : 'in_progress',
                  message: stepContents[stepId] || step.message
                };
              }

              if (currentIdx !== -1 && idx > currentIdx && isProcessStepId(step.id)) {
                return { ...step, status: 'pending' };
              }

              if (step.id === 'feedback' || step.id === 'download') {
                return { ...step, status: 'pending' };
              }

              return step;
            }),
            results: stepContents[stepId] ? {
              success: true,
              message: stepContents[stepId],
              timestamp: new Date().toISOString(),
              allStepContents: { ...stepContents }
            } : prev.results
          };
        });
      }
    };
    
    setRunProgress(prev => {
      const firstPid = PROCESS_STEPS_LIST[0];
      return {
        ...prev,
        isRunning: true,
        status: RUN_STATUS.RUNNING,
        currentStep: firstPid || prev.currentStep,
        error: null,
        steps: prev.steps.map(step => {
          if (step.id === 'start') return { ...step, status: 'completed' };

          if (step.id === 'feedback') return { ...step, status: 'completed', message: undefined };

          if (PROCESS_STEPS_LIST.includes(step.id)) return { ...step, status: 'pending' };

          return step;
        }),
      };
    });
        
    try {
      const result = await WorkflowRunAPI.userFeedback(
        runId,
        userMessage,
        onProgress,
        { signal: controller.signal }
      );
      
      const finalContent = Object.values(stepContents).pop() || '';
      
      if (!humanFeedbackEncountered) {
        setRunProgress(prev => {
          const processStepIds = getProcessStepIds(prev.steps);
          const lastProcessStep = processStepIds[processStepIds.length - 1] || prev.currentStep || 'start';

          return {
            ...prev,
            status: RUN_STATUS.SUCCEEDED,
            currentStep: lastProcessStep,
            isRunning: false,
            steps: prev.steps.map(s => {
              if (isProcessStepId(s.id) || s.id === 'start') {
                return {
                  ...s,
                  status: 'completed',
                  message: stepContents[s.id] || s.message
                };
              }
              return s;
            }),
            results: {
              success: true,
              message: finalContent,
              timestamp: new Date().toISOString(),
              rawData: result,
              allStepContents: { ...stepContents }
            }
          };
        });
      }
      
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Improve content error:', error);
        setRunProgress(prev => ({
          ...prev,
          status: RUN_STATUS.ERROR,
          error: error.message,
          isRunning: false
        }));
      }
    } finally {
      setRunProgress(prev => ({ ...prev, isRunning: false }));
      runController.current = null;
    }
  }, [runProgress.runId, runProgress.isRunning, runProgress.steps]);


  const stopRun = useCallback(() => {
    const controller = runController.current;

    if (controller) {
      controller.abort(); 
      runController.current = null;
    }
    
  setRunProgress(prev => {
      if (prev.status === RUN_STATUS.STOPPED) {
        return prev;
      }

      return {
        ...prev,
        status: RUN_STATUS.STOPPED,
        isRunning: false,
        steps: prev.steps.map(step =>
          step.status === 'in_progress' ? { ...step, status: 'stopped' } : step
        ),
      };
    });
  }, []);

  const goToStep = useCallback((stepId) => {
    setRunProgress(prev => {
      const newStatus = prev.isRunning ? prev.status 
        : stepId === 'download' || prev.status === RUN_STATUS.SUCCEEDED ? RUN_STATUS.SUCCEEDED 
        : RUN_STATUS.WAITING;

      return {
        ...prev,
        currentStep: stepId,
        status: newStatus,
        steps: prev.steps.map(step => {
          if (stepId === 'download') {
            if (step.id === 'feedback') return { ...step, status: 'completed' };
            if (step.id === 'download') return { ...step, status: 'completed' };
          }
          if (stepId === 'feedback' && step.id === 'feedback') {
            return step.status === 'completed' ? step : { ...step, status: 'waiting' };
          }
          return step;
        })
      };
    });
  }, []);

  const deleteWorkflowRun = useCallback(async (runId) => {
    try {
      await WorkflowRunAPI.deleteWorkflowRun(runId);
      return true;
    } catch (error) {
      console.error("Delete error:", error);
      throw error;
    }
  }, []);

  const value = {
    contents, addContent, deleteContent, runProgress, startRun, improveContent, generateMaterials, stopRun, goToStep,
    deleteWorkflowRun, setRunStateFromExistingRun, setRunStateForDownload, setRunStateForFeedback, getWorkflowId, getWorkflowConfig, WORKFLOW_MATRIX,
  };

  return <ContentContext.Provider value={value}>{children}</ContentContext.Provider>;
};

export const useContent = () => {
  const context = useContext(ContentContext);
  if (!context) {
    throw new Error('useContent must be used within ContentProvider');
  }
  return context;
};